import { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const typeColors = { work: { bg: "rgba(16,185,129,0.08)", color: "#10b981", label: "Trabalho" }, off: { bg: "rgba(148,163,184,0.08)", color: "#94a3b8", label: "Folga" }, holiday: { bg: "rgba(59,130,246,0.08)", color: "#3b82f6", label: "Feriado" } };
const DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default function CalendarPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [operators, setOperators] = useState([]);
  const [selectedOp, setSelectedOp] = useState(isAdmin ? "" : "");
  const [month, setMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [schedules, setSchedules] = useState([]);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    if (isAdmin) api.get("/operators").then((r) => setOperators(r.data.filter((o) => o.status === "active"))).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    const params = { month };
    if (selectedOp) params.operator_id = selectedOp;
    api.get("/workforce/schedules", { params }).then((r) => setSchedules(r.data)).catch(() => {});
    api.get("/workforce/entries", { params: { ...params, date: undefined } }).then((r) => {
      setEntries(r.data.filter((e) => e.date.startsWith(month)));
    }).catch(() => {});
  }, [month, selectedOp]);

  const year = parseInt(month.split("-")[0]);
  const mo = parseInt(month.split("-")[1]) - 1;
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const firstDay = new Date(year, mo, 1).getDay();

  const scheduleMap = {};
  schedules.forEach((s) => { scheduleMap[s.date] = s; });
  const entryMap = {};
  entries.forEach((e) => { if (!entryMap[e.date]) entryMap[e.date] = []; entryMap[e.date].push(e); });

  const prevMonth = () => {
    const d = new Date(year, mo - 1, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const nextMonth = () => {
    const d = new Date(year, mo + 1, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const handleDayClick = async (day) => {
    if (!isAdmin || !selectedOp) return;
    const date = `${month}-${String(day).padStart(2, "0")}`;
    const current = scheduleMap[date];
    const types = ["work", "off", "holiday"];
    const currentIdx = types.indexOf(current?.type || "work");
    const nextType = types[(currentIdx + 1) % types.length];
    await api.post("/workforce/schedules", { operator_id: selectedOp, date, type: nextType, start_time: "10:00", end_time: "19:00" });
    const params = { month };
    if (selectedOp) params.operator_id = selectedOp;
    api.get("/workforce/schedules", { params }).then((r) => setSchedules(r.data));
  };

  const monthLabel = new Date(year, mo).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

  return (
    <div data-testid="calendar-page">
      <h1 className="crm-heading mb-6">Calendário</h1>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        {isAdmin && (
          <select value={selectedOp} onChange={(e) => setSelectedOp(e.target.value)} className="crm-input" style={{ maxWidth: "250px" }}>
            <option value="">Todos operadores</option>
            {operators.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        )}
      </div>

      <div className="crm-card overflow-hidden">
        {/* Month navigation */}
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--crm-border)" }}>
          <button onClick={prevMonth} className="p-2 rounded-lg" style={{ color: "var(--crm-text-muted)" }}><ChevronLeft className="w-5 h-5" /></button>
          <p className="text-base font-bold capitalize" style={{ color: "var(--crm-text)" }}>{monthLabel}</p>
          <button onClick={nextMonth} className="p-2 rounded-lg" style={{ color: "var(--crm-text-muted)" }}><ChevronRight className="w-5 h-5" /></button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 text-center">
          {DAYS.map((d) => <div key={d} className="py-2 text-xs font-semibold" style={{ color: "var(--crm-text-dim)", borderBottom: "1px solid var(--crm-border)" }}>{d}</div>)}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {[...Array(firstDay)].map((_, i) => <div key={`e${i}`} className="p-2 min-h-[80px]" style={{ borderBottom: "1px solid var(--crm-border)", borderRight: "1px solid var(--crm-border)" }} />)}
          {[...Array(daysInMonth)].map((_, i) => {
            const day = i + 1;
            const date = `${month}-${String(day).padStart(2, "0")}`;
            const sch = scheduleMap[date];
            const dayEntries = entryMap[date] || [];
            const tc = typeColors[sch?.type || "work"] || typeColors.work;
            const hasIssue = dayEntries.some((e) => e.alerts?.length > 0);
            const isToday = date === new Date().toISOString().slice(0, 10);

            return (
              <div key={day} onClick={() => handleDayClick(day)}
                className="p-2 min-h-[80px] transition-colors"
                style={{
                  borderBottom: "1px solid var(--crm-border)", borderRight: "1px solid var(--crm-border)",
                  background: isToday ? "var(--crm-accent-subtle)" : tc.bg,
                  cursor: isAdmin && selectedOp ? "pointer" : "default",
                }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold" style={{ color: isToday ? "var(--crm-accent)" : "var(--crm-text)" }}>{day}</span>
                  {sch && sch.type !== "work" && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: tc.bg, color: tc.color }}>{tc.label}</span>}
                </div>
                {dayEntries.map((e) => (
                  <div key={e.id} className="text-[10px] mb-0.5 px-1 py-0.5 rounded" style={{ background: hasIssue ? "rgba(239,68,68,0.08)" : "rgba(16,185,129,0.06)", color: hasIssue ? "#ef4444" : "#10b981" }}>
                    {e.clock_in?.slice(0,5)}—{e.clock_out?.slice(0,5) || "..."} {e.total_hours ? `(${e.total_hours}h)` : ""}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="px-5 py-3 flex flex-wrap gap-4 text-xs" style={{ borderTop: "1px solid var(--crm-border)" }}>
          {Object.entries(typeColors).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5"><div className="w-3 h-3 rounded" style={{ background: v.color }} />{v.label}</div>
          ))}
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded" style={{ background: "#ef4444" }} />Alerta</div>
          {isAdmin && selectedOp && <span style={{ color: "var(--crm-text-dim)" }}>Clique no dia para alternar: Trabalho → Folga → Feriado</span>}
        </div>
      </div>
    </div>
  );
}
